// FutureTec-v2 demo API mock layer.
// Phase 1.2b (2026-02) — the sandbox ships as a static build (Wingman / Vercel);
// there is no backend at the deployed URL, and the shared preview backend
// rejects cross-origin credentialed requests. To keep the demo interactive we
// intercept axios calls to a small allowlist of endpoints and reply from
// fixtures.
//
// Design:
//   - Registers ONE axios request interceptor that resolves matched requests
//     with a synthetic AxiosResponse via `config.adapter`. Non-matched
//     requests pass through untouched.
//   - Only reads/writes the axios instance exported from ./api — no globals.
//   - No routes to `/api/lessons/*/audio` or PDF downloads (those are handled
//     as <audio src=…> / anchor navigations, not axios).
//
// To disable at build time: set REACT_APP_DISABLE_MOCKS=true.

import { api } from "./api";

const FIXTURES = {
  ageBands: {
    age_bands: [
      { code: "6-7",   label: "Ages 6-7",   enrolled: 12, cap: 30 },
      { code: "8-9",   label: "Ages 8-9",   enrolled: 23, cap: 30 },
      { code: "10-12", label: "Ages 10-12", enrolled: 18, cap: 30 },
      { code: "13-14", label: "Ages 13-14", enrolled: 9,  cap: 30 },
      { code: "15-17", label: "Ages 15-17", enrolled: 5,  cap: 30 },
    ],
  },
  socialProof: {
    recent_enrollments: [
      { name: "Priya",  country: "LK", when: "2 min ago"  },
      { name: "Rahim",  country: "MY", when: "6 min ago"  },
      { name: "Suchen", country: "HK", when: "14 min ago" },
      { name: "Aroon",  country: "TH", when: "28 min ago" },
      { name: "Tahmid", country: "BD", when: "42 min ago" },
    ],
  },
  founderCount:  { count: 47, cap: 100 },
  authMe: {
    id: "demo-user",
    email: "demo@futuretec.global",
    parent_name: "Demo Parent",
    role: "parent",
  },
  enrollmentsMe: {
    enrollments: [
      {
        id: "demo-enrollment-1",
        age_band: "age_8-9",
        tier: "trial",
        terms_unlocked: [1],
        lesson_progress: {},
      },
    ],
  },
  showcaseOverview: {
    totals: { lessons: 300, dramas: 20, readers: 75, workbooks: 15 },
    bands: [
      { slug: "age_6-7",   label_dash: "Ages 6-7",   lesson_count: 60, reader_count: 15, audio_drama_count: 4, workbook_count: 3, lessons: [], readers: [], audio_dramas: [], workbooks: [] },
      { slug: "age_8-9",   label_dash: "Ages 8-9",   lesson_count: 60, reader_count: 15, audio_drama_count: 4, workbook_count: 3, lessons: [], readers: [], audio_dramas: [], workbooks: [] },
      { slug: "age_10-12", label_dash: "Ages 10-12", lesson_count: 60, reader_count: 15, audio_drama_count: 4, workbook_count: 3, lessons: [], readers: [], audio_dramas: [], workbooks: [] },
      { slug: "age_13-14", label_dash: "Ages 13-14", lesson_count: 60, reader_count: 15, audio_drama_count: 4, workbook_count: 3, lessons: [], readers: [], audio_dramas: [], workbooks: [] },
      { slug: "age_15-17", label_dash: "Ages 15-17", lesson_count: 60, reader_count: 15, audio_drama_count: 4, workbook_count: 3, lessons: [], readers: [], audio_dramas: [], workbooks: [] },
    ],
  },
  previewStatus: { is_owner_preview: false },
  lessonsEmpty:  { lessons: [] },
  readersEmpty:  { chapters: [] },
};

// Return a synthetic AxiosResponse.
function ok(config, data, status = 200) {
  return { data, status, statusText: "OK", headers: {}, config, request: null };
}
function unauthorized(config) {
  const err = new Error("Request failed with status code 401");
  err.response = { data: { detail: "Unauthorized" }, status: 401, statusText: "Unauthorized", headers: {}, config };
  err.config = config;
  err.isAxiosError = true;
  return Promise.reject(err);
}

// Match request against known mocked endpoints. Returns a promise resolving to
// an AxiosResponse, or `null` if the request should pass through.
function matchMock(config) {
  const method = (config.method || "get").toLowerCase();
  // config.url can be relative (`/catalog/age-bands`) since baseURL is set.
  const url = (config.url || "").split("?")[0];

  if (method === "get") {
    if (url.endsWith("/catalog/age-bands"))          return Promise.resolve(ok(config, FIXTURES.ageBands));
    if (url.endsWith("/checkout/social-proof"))      return Promise.resolve(ok(config, FIXTURES.socialProof));
    if (url.endsWith("/enrollments/founder-count"))  return Promise.resolve(ok(config, FIXTURES.founderCount));
    if (url.endsWith("/auth/me"))                    return Promise.resolve(ok(config, FIXTURES.authMe));
    if (url.endsWith("/enrollments/me"))             return Promise.resolve(ok(config, FIXTURES.enrollmentsMe));
    if (url.endsWith("/showcase/overview"))          return Promise.resolve(ok(config, FIXTURES.showcaseOverview));
    if (url.endsWith("/preview/status"))             return Promise.resolve(ok(config, FIXTURES.previewStatus));
    if (url.match(/\/catalog\/[^/]+\/[^/]+\/lessons$/))   return Promise.resolve(ok(config, FIXTURES.lessonsEmpty));
    if (url.match(/\/readers\/[^/]+\/term_[^/]+$/))       return Promise.resolve(ok(config, FIXTURES.readersEmpty));
  }
  if (method === "post") {
    if (url.endsWith("/auth/register")) {
      return Promise.resolve(ok(config, {
        access_token: "demo.mock.token", token_type: "bearer",
        user: { id: "demo-user", email: (config.data && JSON.parse(config.data).email) || "demo@futuretec.global", role: "parent" },
      }, 201));
    }
    if (url.endsWith("/auth/login")) {
      return Promise.resolve(ok(config, {
        access_token: "demo.mock.token", token_type: "bearer",
        user: { id: "demo-user", email: (config.data && JSON.parse(config.data).email) || "demo@futuretec.global", role: "parent" },
      }));
    }
    if (url.endsWith("/auth/forgot-password")) {
      return Promise.resolve(ok(config, { ok: true, message: "Reset link generated (demo mode)." }));
    }
  }
  return null;
}

let installed = false;

// Match a URL against the same endpoint allowlist as the axios interceptor,
// returning a Response for window.fetch to resolve. Kept in-sync with matchMock.
function fetchMockResponse(input, init) {
  const url = typeof input === "string" ? input : input.url;
  const method = ((init && init.method) || (typeof input === "object" && input.method) || "GET").toUpperCase();
  const path = url.split("?")[0];
  const json = (data, status = 200) =>
    new Response(JSON.stringify(data), { status, headers: { "content-type": "application/json" } });
  if (method === "GET") {
    if (path.endsWith("/api/showcase/overview")) return json(FIXTURES.showcaseOverview);
    if (path.endsWith("/api/preview/status"))    return json(FIXTURES.previewStatus);
    if (path.endsWith("/api/auth/me"))           return json(FIXTURES.authMe);
    if (path.endsWith("/api/catalog/age-bands")) return json(FIXTURES.ageBands);
    if (path.endsWith("/api/checkout/social-proof"))     return json(FIXTURES.socialProof);
    if (path.endsWith("/api/enrollments/founder-count")) return json(FIXTURES.founderCount);
  }
  return null;
}

export function installMocks() {
  if (installed) return;
  if (process.env.REACT_APP_DISABLE_MOCKS === "true") return;
  // Axios path (used by most of the app).
  const defaultAdapter = api.defaults.adapter;
  api.defaults.adapter = (config) => {
    const mocked = matchMock(config);
    if (mocked) return mocked;
    return defaultAdapter(config);
  };
  // window.fetch path (used by Showcase for /api/showcase/overview and /api/preview/status).
  if (typeof window !== "undefined" && window.fetch) {
    const origFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const mocked = fetchMockResponse(input, init);
      if (mocked) return Promise.resolve(mocked);
      return origFetch(input, init);
    };
  }
  installed = true;
  // eslint-disable-next-line no-console
  console.info("[ftg] demo API mocks installed (Phase 1.2b)");
}
